/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tag, Tags } from 'store/types';
import style from './searchDropdown.module.scss';
import map from 'lodash-es/map';
import each from 'lodash-es/each';
import isEmpty from 'lodash-es/isEmpty';
import keys from 'lodash-es/keys';
import { animated, useSpring } from 'react-spring';
import { DURATIONS, isSearchEnoughtLarge } from 'utils/utils';
import TagsContainer from 'components/tags/tagsContainer';
import { SearchSection } from 'controls/search/helperSearch/helperSearch';
import { useAppSelector } from 'store/hooks';
import { selectIsPending } from 'store/tagsSlice';
import { size } from 'lodash-es';
import { GetChipFunction } from 'hooks/useSearchControl';
import CircularLoader from 'components/loader/circular';
import cx from 'classnames';
import classNames from 'classnames';
import { useRegisterKeyHandler } from 'hooks/useKeyHandler';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { tagsWrapTransition } from 'components/transitionWrappers/tagsWrapTransition';

const MAX_HEIGHT = 250;

export default function SearchDropdown({
  isOpen,
  setIsOpen,
  sectionsOrder,
  selected,
  available,
  overrideSectionTexts,
  getChip,
  search,
  availableHash,
  top,
  hideEmptyAvailable,
  keyHandlerId,
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  sectionsOrder: SearchSection[];
  selected: Tags;
  available: Tags;
  overrideSectionTexts?: { [x: string]: string };
  getChip: GetChipFunction;
  search: string;
  availableHash: string;
  top: Tags;
  keyHandlerId: string;

  // Не показывать блок с результатом поиска, если он пуст
  hideEmptyAvailable?: boolean;
}) {
  const createCandidate: Tag = {
    name: search,
    isNew: true,
  };

  const getActiveSections = () => {
    const ac: Tags[] = [];

    each(sectionsOrder, (sectionName) => {
      switch (sectionName) {
        case 'available':
          if (!isEmpty(available)) {
            ac.push(available);
          }
          break;
        case 'top':
          if (!isEmpty(top) && (isEmpty(available) || !search)) {
            ac.push(top);
          }
          break;
        case 'create':
          if (!isEmpty(createCandidate.name)) {
            ac.push({ [createCandidate.name]: createCandidate });
          }
          break;
      }
    });

    return ac;
  };

  // Секции, по которым можно передвигаться клавишами
  const [activeSections, setActiveSections] = useState(getActiveSections());
  // Секции упорядочены и есть индекс текущей секции и сама секция
  const [currentSectionInd, setCurrentSectionInd] = useState(0);
  const currentSection = activeSections[currentSectionInd];

  // Смещение внутри секции
  const [focusedOffset, setFocusedOffset] = useState(0);
  // ID тега с фокусом
  const [focusedId, setFocusedId] = useState(
    keys(currentSection)[focusedOffset]
  );
  const focusedRef = useRef<HTMLDivElement>(null);
  const isPending = useAppSelector(selectIsPending);

  // В момент изменения секций, сбрасываем на самую верхнюю
  useEffect(() => {
    setCurrentSectionInd(0);
  }, [size(activeSections)]);

  // Пересчитываем секции
  useEffect(() => {
    setActiveSections(getActiveSections());
  }, [available, top, sectionsOrder, createCandidate.name]);

  useEffect(() => {
    const focusedCandidate = keys(currentSection)[focusedOffset];

    if (focusedCandidate) {
      setFocusedId(focusedCandidate);
    } else {
      setFocusedId(keys(currentSection)[focusedOffset]);
      setFocusedOffset(0);
    }
  }, [focusedOffset, currentSection]);

  useEffect(() => {
    setFocusedOffset(0);
  }, [availableHash]);

  const keyHandler = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) {
        return;
      }

      switch (e.code) {
        case 'Enter':
          e.preventDefault();
          e.stopPropagation();
          if (focusedRef?.current) {
            focusedRef.current.click();
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          e.stopPropagation();
          if (focusedOffset === 0) {
            setFocusedOffset(0);
          } else {
            setFocusedOffset(focusedOffset - 1);
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          e.stopPropagation();
          if (focusedOffset === size(currentSection) - 1) {
            setFocusedOffset(size(currentSection) - 1);
          } else {
            setFocusedOffset(focusedOffset + 1);
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          if (currentSectionInd <= 0) {
            setCurrentSectionInd(0);
          } else {
            setCurrentSectionInd(currentSectionInd - 1);
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          if (currentSectionInd >= activeSections.length - 1) {
            setCurrentSectionInd(0);
          } else {
            setCurrentSectionInd(currentSectionInd + 1);
          }
          break;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(false);
          break;
      }
    },
    [
      isOpen,
      currentSection,
      focusedOffset,
      currentSectionInd,
      activeSections.length,
    ]
  );

  useRegisterKeyHandler({
    handler: keyHandler,
    id: keyHandlerId,
  });

  const sectionsText = overrideSectionTexts || {
    selected: 'Выбрано',
    available: 'Похожие теги',
    create: 'Добавить новый тег',
    top: 'Популярные теги',
  };

  const sections = {
    selected: !isEmpty(selected) && (
      <div key="0" className={style.section}>
        <span>{sectionsText.selected}</span>
        <TransitionGroup component={TagsContainer}>
          {tagsWrapTransition({
            tags: selected,
            getChip,
          })}
        </TransitionGroup>
      </div>
    ),
    available:
      hideEmptyAvailable && isEmpty(available) ? (
        ''
      ) : (
        <div key="1" className={style.section}>
          <span>{sectionsText.available}</span>
          {isEmpty(available) ? (
            <span className={style.notFound}>
              {search
                ? isSearchEnoughtLarge(search)
                  ? 'Поиск не даёт похожих результатов. Очистите строку и попробуйте снова'
                  : 'Поиск начинается от 3 символов'
                : 'Начните ввод, чтобы увидеть похожие теги'}
            </span>
          ) : (
            <TagsContainer>
              {map(available, (tag) => {
                return getChip(
                  {
                    isDeletable: false,
                    isFocused: tag.name === focusedId,
                    ref: tag.name === focusedId ? focusedRef : undefined,
                  },
                  tag
                );
              })}
            </TagsContainer>
          )}
        </div>
      ),
    create: search &&
      isEmpty(available[search]) &&
      isEmpty(selected[search]) && (
        <div key="2" className={style.section}>
          <span>{sectionsText.create}</span>

          {isSearchEnoughtLarge(search) ? (
            <TagsContainer>
              {getChip(
                {
                  isDeletable: false,
                  isFocused: createCandidate.name === focusedId,
                  ref:
                    createCandidate.name === focusedId ? focusedRef : undefined,
                },
                createCandidate
              )}
            </TagsContainer>
          ) : (
            <span className={style.notFound}>
              Минимальная длина создаваемого тега 3 символа
            </span>
          )}
        </div>
      ),
    // Тот же available, только со смещенным в конец порядком
    top: (isEmpty(available) || !search) && (
      <div key="3" className={style.section}>
        <span>{sectionsText.top}</span>
        <TagsContainer>
          {map(top, (tag) => {
            return getChip(
              {
                isDeletable: false,
                isFocused: tag.name === focusedId,
                ref: tag.name === focusedId ? focusedRef : undefined,
              },
              tag
            );
          })}
        </TagsContainer>
      </div>
    ),
  };

  const ref = useRef<HTMLDivElement>(null);

  let currentHeight = `${MAX_HEIGHT}px`;

  if (ref.current) {
    const currentMax = ref.current.style.maxHeight;
    const currentPaddingTop = ref.current.style.paddingTop;
    const currentPaddingBottom = ref.current.style.paddingBottom;

    ref.current.style.maxHeight = `${MAX_HEIGHT}px`;
    ref.current.style.paddingTop = '28px';
    ref.current.style.paddingBottom = '28px';

    const rect = ref.current.getBoundingClientRect();
    const realHeight = rect.height;

    ref.current.style.maxHeight = currentMax;
    ref.current.style.paddingTop = currentPaddingTop;
    ref.current.style.paddingBottom = currentPaddingBottom;

    currentHeight = realHeight > MAX_HEIGHT ? currentHeight : `${realHeight}px`;
  }

  const anime = useSpring({
    maxHeight: !isOpen ? '0px' : currentHeight,
    paddingTop: isOpen ? '28px' : '0px',
    paddingBottom: isOpen ? '28px' : '0px',
    config: { duration: DURATIONS.SEARCH },
  });

  return (
    <animated.div
      style={anime}
      className={cx(style.container, isOpen ? style.shadow : '')}
      ref={ref}
    >
      {isPending.match ? (
        <>
          <div className={style.overlay}></div>
          <CircularLoader></CircularLoader>
        </>
      ) : (
        ''
      )}
      {map(sectionsOrder, (sect) => sections[sect])}
    </animated.div>
  );
}
